import type { AxiosInstance } from "axios";

export type SessionEventCounts = Record<string, number>;

const DEFAULT_FLUSH_INTERVAL_MS = 30_000;

let pending: SessionEventCounts = {};
let axiosInstance: AxiosInstance | null = null;
let getToken: (() => string | null) | null = null;
let flushIntervalMs = DEFAULT_FLUSH_INTERVAL_MS;
let intervalId: number | null = null;
let isFlushing = false;

export function configureSessionEvents(opts: {
  axios: AxiosInstance | null | undefined;
  getToken: (() => string | null) | null | undefined;
  flushIntervalMs?: number;
}): void {
  axiosInstance = opts.axios ?? null;
  getToken = opts.getToken ?? null;
  if (typeof opts.flushIntervalMs === "number" && Number.isFinite(opts.flushIntervalMs) && opts.flushIntervalMs > 0) {
    flushIntervalMs = opts.flushIntervalMs;
  }
}

export function trackSessionEvent(name: string, count = 1): void {
  const key = String(name ?? "").trim();
  if (!key) return;
  const delta = Number(count ?? 1);
  if (!Number.isFinite(delta) || delta <= 0) return;
  pending[key] = (pending[key] || 0) + delta;
}

export function getPendingSessionEvents(): SessionEventCounts {
  return { ...pending };
}

export async function flushSessionEvents(): Promise<void> {
  if (isFlushing) return;
  if (!axiosInstance || !getToken) return;

  const keys = Object.keys(pending);
  if (keys.length === 0) return;

  const token = getToken();
  if (!token) return;

  const snapshot: SessionEventCounts = {};
  for (const k of keys) {
    const v = pending[k];
    if (typeof v === "number" && Number.isFinite(v) && v > 0) snapshot[k] = v;
  }
  if (Object.keys(snapshot).length === 0) return;

  isFlushing = true;
  try {
    await axiosInstance.post(
      "/session/events",
      { events: snapshot },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    // Only clear what we successfully sent; keep any events recorded during the flush.
    for (const [k, v] of Object.entries(snapshot)) {
      const cur = pending[k] || 0;
      const next = cur - v;
      if (next > 0) pending[k] = next;
      else delete pending[k];
    }
  } catch {
    // Best-effort: keep pending events for a later retry.
  } finally {
    isFlushing = false;
  }
}

export function startSessionEvents(): void {
  if (intervalId != null) return;
  // Flush periodically; also flush once shortly after start to avoid long delays.
  intervalId = window.setInterval(() => {
    void flushSessionEvents();
  }, flushIntervalMs) as unknown as number;
  window.setTimeout(() => void flushSessionEvents(), 1500);
}

export function stopSessionEvents(opts?: { clearPending?: boolean }): void {
  if (intervalId != null) {
    clearInterval(intervalId);
    intervalId = null;
  }
  if (opts?.clearPending) {
    pending = {};
  }
  isFlushing = false;
}

